import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconChevronDown,
  IconNotebook,
} from "@tabler/icons-react";
import MInputField from "@/components/ui/atoms/MInputField";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import MTabs from "@/components/ui/molecules/MTabs";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import MDropDownDrawer, {
  DropdownItem,
} from "@/components/ui/molecules/MDropDownDrawer";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { useMedicines } from "@/hooks/useMedicine";
import { Medicine } from "@/types/medicine";
import {
  CreateMedicineItemPayload,
  DayFrequency,
  DaytimeSlot,
  FoodContext,
  MedicineItemType,
  MedicineStatus,
  MedicineTimingRuleType,
  PillboxItem,
} from "@/types/pillbox";
import CategoryPicker, { MedicineCategory } from "./components/CategoryPicker";
import DaytimeMultiSelect from "./components/DaytimeMultiSelect";
import WeekdayChips from "./components/WeekdayChips";
import DuplicateMedicineModal from "./components/DuplicateMedicineModal";
import { deriveSlotFromTime } from "./utils/formatMedicine";
import styles from "./AddMedicine.module.css";

type RepeatsOption =
  | "EVERY_DAY"
  | "EVERY_OTHER_DAY"
  | "SPECIFIC_DAYS"
  | "EVERY_X_DAYS";

type FormMode = "add" | "edit" | "restore" | "review";

interface AddMedicineNavState {
  defaultCategory?: MedicineCategory;
  mode?: FormMode;
  item?: PillboxItem;
  pendingId?: string;
  siblingPendingIds?: string[];
  /** List/Schedule tab the landing screen was on before opening this form
   *  in "add" mode (see PillboxLanding's handleAdd) — forwarded back so
   *  returning to the landing screen restores it. Not meaningful for
   *  edit/restore/review, which aren't opened from the landing screen's FAB. */
  fromView?: "schedule" | "list";
}

const DAYTIME_SLOT_TIMES: Record<DaytimeSlot, string> = {
  morning: "08:00",
  afternoon: "13:00",
  evening: "17:00",
  night: "21:00",
};

const SLOT_COUNT_TO_DAY_FREQUENCY: Record<number, DayFrequency> = {
  1: DayFrequency.ONCE,
  2: DayFrequency.TWICE,
  3: DayFrequency.THRICE,
  4: DayFrequency.FOUR_TIMES,
};

function toDateOnlyIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function deriveName(item: PillboxItem): string {
  return item.dose ? `${item.name} ${item.dose}` : item.name;
}

function deriveRepeats(item: PillboxItem): {
  repeatsOption: RepeatsOption;
  everyXDays: string;
  selectedWeekdays: number[];
} {
  if (item.selectedWeekdays && item.selectedWeekdays.length > 0) {
    return {
      repeatsOption: "SPECIFIC_DAYS",
      everyXDays: "2",
      selectedWeekdays: item.selectedWeekdays,
    };
  }
  const interval = item.recurrenceIntervalDays ?? 1;
  if (interval === 2) {
    return {
      repeatsOption: "EVERY_OTHER_DAY",
      everyXDays: "2",
      selectedWeekdays: [],
    };
  }
  if (interval > 2) {
    return {
      repeatsOption: "EVERY_X_DAYS",
      everyXDays: String(interval),
      selectedWeekdays: [],
    };
  }
  return { repeatsOption: "EVERY_DAY", everyXDays: "2", selectedWeekdays: [] };
}

// Keyed by location.key so navigating from the duplicate-check modal back
// into this same route (edit/restore mode) forces a full remount instead of
// reusing stale form state from the "add" attempt that was just abandoned.
const AddMedicine: React.FC = () => {
  const location = useLocation();
  return <AddMedicineForm key={location.key} />;
};

const AddMedicineForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  const {
    addMedicine,
    updateItem,
    resumeItem,
    fetchLedger,
    confirmPending,
    rejectPending,
  } = usePillbox(memberId);
  const {
    searchMedicines,
    searchResults,
    searchLoading,
    searchHasMore,
    loadMoreSearchResults,
  } = useMedicines();

  const navState = location.state as AddMedicineNavState | null;
  const mode: FormMode = navState?.mode ?? "add";
  const existingItem = navState?.item ?? null;
  const pendingId = navState?.pendingId;
  const siblingPendingIds = navState?.siblingPendingIds;
  const wasSos =
    existingItem?.timingRuleType === MedicineTimingRuleType.CONDITIONAL;

  const initialRepeats = existingItem
    ? deriveRepeats(existingItem)
    : {
        repeatsOption: "EVERY_DAY" as RepeatsOption,
        everyXDays: "2",
        selectedWeekdays: [],
      };

  const [category, setCategory] = useState<MedicineCategory>(
    existingItem
      ? existingItem.timingRuleType === MedicineTimingRuleType.CONDITIONAL
        ? "SOS"
        : "SCHEDULED"
      : navState?.defaultCategory ?? "SCHEDULED"
  );
  const [itemType, setItemType] = useState<MedicineItemType>(
    existingItem?.itemType ?? MedicineItemType.MEDICINE
  );
  const [name, setName] = useState(
    existingItem ? deriveName(existingItem) : ""
  );
  const [repeatsOption, setRepeatsOption] = useState<RepeatsOption>(
    initialRepeats.repeatsOption
  );
  const [repeatsDrawerOpen, setRepeatsDrawerOpen] = useState(false);
  const [everyXDays, setEveryXDays] = useState(initialRepeats.everyXDays);
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
    initialRepeats.selectedWeekdays
  );
  const [selectedSlots, setSelectedSlots] = useState<DaytimeSlot[]>(
    existingItem ? (existingItem.slots ?? []).map(deriveSlotFromTime) : []
  );
  const prefillDetails = mode === "edit" || mode === "review";
  const [reasonForTaking, setReasonForTaking] = useState(
    prefillDetails ? existingItem?.reasonForTaking ?? "" : ""
  );
  const [startDate, setStartDate] = useState<Date>(
    prefillDetails && existingItem?.startDate
      ? new Date(existingItem.startDate)
      : new Date()
  );
  const [showMoreDetails, setShowMoreDetails] = useState(mode !== "add");
  const [endDate, setEndDate] = useState<Date | undefined>(
    prefillDetails && existingItem?.endDate
      ? new Date(existingItem.endDate)
      : undefined
  );
  const [doctorName, setDoctorName] = useState(
    prefillDetails ? existingItem?.doctorName ?? "" : ""
  );
  const [foodContext, setFoodContext] = useState<FoodContext | undefined>(
    existingItem?.foodContext ?? undefined
  );
  const [foodContextDrawerOpen, setFoodContextDrawerOpen] = useState(false);
  const [notes, setNotes] = useState(
    prefillDetails ? existingItem?.notes ?? "" : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState<{
    variant: "active" | "past";
    item: PillboxItem;
  } | null>(null);
  const [showMoveToScheduledConfirm, setShowMoveToScheduledConfirm] =
    useState(false);

  const repeatsOptions: DropdownItem[] = [
    { value: "EVERY_DAY", label: t("pillbox.repeats.everyDay") },
    { value: "EVERY_OTHER_DAY", label: t("pillbox.repeats.everyOtherDay") },
    { value: "SPECIFIC_DAYS", label: t("pillbox.repeats.specificDays") },
    { value: "EVERY_X_DAYS", label: t("pillbox.repeats.everyXDays") },
  ];

  const foodContextOptions: DropdownItem[] = [
    { value: FoodContext.BEFORE_FOOD, label: t("pillbox.foodContext.before") },
    { value: FoodContext.AFTER_FOOD, label: t("pillbox.foodContext.after") },
    { value: FoodContext.WITH_FOOD, label: t("pillbox.foodContext.with") },
  ];

  // MDatePicker defaults its max-selectable date to today when no maxDate
  // is passed — without this, start/stop dates couldn't be set in the
  // future at all. Past dates are unaffected (MDatePicker's minYear
  // defaults to 1900, already permissive).
  const maxAllowedDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return d;
  }, []);

  const repeatsLabel = repeatsOptions.find((o) => o.value === repeatsOption)
    ?.label as string;
  const foodContextLabel = foodContext
    ? (foodContextOptions.find((o) => o.value === foodContext)?.label as string)
    : undefined;

  // A SCHEDULED medicine with no daytime slots selected would save
  // successfully but never generate a dose-event — require at least one
  // slot so the form can't produce a medicine that silently never shows up
  // on the schedule.
  const isValid =
    name.trim().length > 0 &&
    (category !== "SCHEDULED" || selectedSlots.length > 0);

  const getMedicineOptionLabel = (med: Medicine) => med.medicine_name;

  const renderMedicineOption = (med: Medicine) => (
    <div>
      <div className="font-medium text-gray-900">{med.medicine_name}</div>
      {med.dosage && <div className="text-xs text-gray-500">{med.dosage}</div>}
    </div>
  );

  const handleMedicineSelect = (item: any) => {
    if (typeof item === "string") {
      setName(item);
    } else {
      setName(
        item.dosage
          ? `${item.medicine_name} ${item.dosage}`
          : item.medicine_name
      );
    }
  };

  const handleSubmit = () => {
    if (!isValid || submitting) return;
    if (mode === "review" && (!existingItem || !pendingId)) return;
    if (mode === "edit" && wasSos && category === "SCHEDULED") {
      setShowMoveToScheduledConfirm(true);
      return;
    }
    performSubmit();
  };

  const performSubmit = async () => {
    setSubmitting(true);
    try {
      const isScheduled = category === "SCHEDULED";
      const slots = isScheduled
        ? selectedSlots.map((s) => DAYTIME_SLOT_TIMES[s]).sort()
        : [];

      const payload: CreateMedicineItemPayload = {
        name: name.trim(),
        timingRuleType: isScheduled
          ? MedicineTimingRuleType.FIXED_SLOTS
          : MedicineTimingRuleType.CONDITIONAL,
        slots: isScheduled ? slots : undefined,
        dayFrequency: isScheduled
          ? SLOT_COUNT_TO_DAY_FREQUENCY[slots.length]
          : undefined,
        recurrenceIntervalDays:
          isScheduled && repeatsOption !== "SPECIFIC_DAYS"
            ? repeatsOption === "EVERY_OTHER_DAY"
              ? 2
              : repeatsOption === "EVERY_X_DAYS"
              ? Math.max(1, parseInt(everyXDays, 10) || 1)
              : 1
            : undefined,
        selectedWeekdays:
          isScheduled && repeatsOption === "SPECIFIC_DAYS"
            ? selectedWeekdays
            : undefined,
        reasonForTaking: reasonForTaking.trim() || undefined,
        startDate: toDateOnlyIso(startDate),
        endDate: endDate ? toDateOnlyIso(endDate) : undefined,
        doctorName: doctorName.trim() || undefined,
        foodContext,
        notes: notes.trim() || undefined,
      };

      if (mode === "add") {
        const nameKey = name.trim().toLowerCase();
        const ledger = await fetchLedger();
        const activeMatch = ledger.find(
          (i) =>
            i.itemType === itemType &&
            i.status === MedicineStatus.ACTIVE &&
            i.name.trim().toLowerCase() === nameKey
        );
        const pastMatch =
          !activeMatch &&
          ledger.find(
            (i) =>
              i.itemType === itemType &&
              i.status === MedicineStatus.STOPPED &&
              i.name.trim().toLowerCase() === nameKey
          );
        if (activeMatch) {
          setDuplicateModal({ variant: "active", item: activeMatch });
          return;
        }
        if (pastMatch) {
          setDuplicateModal({ variant: "past", item: pastMatch });
          return;
        }
        await addMedicine(itemType, payload);
      } else if (mode === "edit" && existingItem) {
        await updateItem(existingItem.itemType, existingItem.id, payload);
      } else if (mode === "restore" && existingItem) {
        await resumeItem(existingItem.itemType, existingItem.id, payload);
      } else if (mode === "review" && pendingId) {
        await confirmPending(pendingId, payload);
        if (siblingPendingIds && siblingPendingIds.length > 0) {
          await Promise.allSettled(
            siblingPendingIds.map((id) => rejectPending(id))
          );
        }
      }

      const successKey =
        mode === "edit"
          ? "pillbox.addMedicine.successMessageEdit"
          : mode === "restore"
          ? "pillbox.addMedicine.successMessageRestore"
          : isScheduled
          ? "pillbox.addMedicine.successMessage"
          : "pillbox.addMedicine.successMessageSos";
      showToast({
        variant: "success",
        message: t(successKey, {
          name: name.trim(),
          member: memberDisplayName,
        }),
      });
      if (mode === "review") {
        navigate("/pillbox/review");
      } else if (isScheduled) {
        navigate("/pillbox", { state: { viewMode: navState?.fromView } });
      } else {
        navigate("/pillbox/sos");
      }
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveToScheduledConfirm = () => {
    setShowMoveToScheduledConfirm(false);
    performSubmit();
  };

  const handleDuplicateConfirm = () => {
    if (!duplicateModal) return;
    navigate("/pillbox/add-medicine", {
      replace: true,
      state: {
        mode: duplicateModal.variant === "active" ? "edit" : "restore",
        item: duplicateModal.item,
      } as AddMedicineNavState,
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() =>
              // "add" mode is only ever opened from the Pillbox landing
              // screen's FAB — go back there explicitly, restoring its
              // List/Schedule tab. Other modes (edit/restore/review) can be
              // opened from various places (MedicineDetail, the duplicate
              // modal, Review Medications), so navigate(-1) is still the
              // correct general "wherever I came from" behavior there.
              mode === "add"
                ? navigate("/pillbox", {
                    state: { viewMode: navState?.fromView },
                  })
                : navigate(-1)
            }
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>
            {t(
              mode === "edit"
                ? "pillbox.addMedicine.editTitle"
                : mode === "restore"
                ? "pillbox.addMedicine.restoreTitle"
                : mode === "review"
                ? "pillbox.addMedicine.reviewTitle"
                : "pillbox.addMedicine.title"
            )}
          </h1>
          <div className={styles.iconSpacer} />
        </div>
        <p className={styles.subtitle}>
          {t(
            mode === "edit"
              ? "pillbox.addMedicine.editSubtitle"
              : mode === "restore"
              ? "pillbox.addMedicine.restoreSubtitle"
              : mode === "review"
              ? "pillbox.addMedicine.reviewSubtitle"
              : "pillbox.addMedicine.subtitle",
            { member: memberDisplayName }
          )}
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.field}>
          <span className={styles.smallLabel}>
            {t("pillbox.addMedicine.selectCategory")}
          </span>
          <CategoryPicker value={category} onChange={setCategory} />
        </div>

        <div className={styles.field}>
          <span className={styles.smallLabel}>
            {t("pillbox.addMedicine.selectType")}
          </span>
          <MTabs
            activeTab={itemType}
            onTabChange={(v) => setItemType(v as MedicineItemType)}
            disabled={mode === "edit" || mode === "restore"}
            tabs={[
              {
                value: MedicineItemType.MEDICINE,
                label: t("pillbox.addMedicine.medicine"),
              },
              {
                value: MedicineItemType.SUPPLEMENT,
                label: t("pillbox.addMedicine.supplement"),
              },
            ]}
          />
        </div>

        <AutoCompleteInput
          label={t("pillbox.addMedicine.nameLabel")}
          placeholder={t("pillbox.addMedicine.namePlaceholder")}
          data={searchResults}
          getOptionLabel={getMedicineOptionLabel}
          renderOption={renderMedicineOption}
          onSelect={handleMedicineSelect}
          value={name}
          onChange={setName}
          loading={searchLoading}
          onSearch={searchMedicines}
          minLength={3}
          onLoadMore={loadMoreSearchResults}
          hasMore={searchHasMore}
        />

        {category === "SCHEDULED" && (
          <>
            <MInputField
              label={t("pillbox.addMedicine.repeats")}
              value={repeatsLabel}
              onClick={() => setRepeatsDrawerOpen(true)}
              readOnly
              size="md"
              variant="Underlined"
              icon={<IconChevronDown size={16} />}
              showIcon
              onIconClick={() => setRepeatsDrawerOpen(true)}
            />
            {repeatsOption === "EVERY_X_DAYS" && (
              <MInputField
                label={t("pillbox.addMedicine.everyXDaysLabel")}
                value={everyXDays}
                onChange={(e) =>
                  setEveryXDays(e.target.value.replace(/\D/g, ""))
                }
                type="number"
                size="md"
                variant="Underlined"
              />
            )}
            {repeatsOption === "SPECIFIC_DAYS" && (
              <WeekdayChips
                selected={selectedWeekdays}
                onChange={setSelectedWeekdays}
              />
            )}

            <div className={styles.field}>
              <span className={styles.label}>
                {t("pillbox.addMedicine.timesADay")}
              </span>
              <DaytimeMultiSelect
                selected={selectedSlots}
                onChange={setSelectedSlots}
              />
            </div>
          </>
        )}

        <MInputField
          label={t("pillbox.addMedicine.reasonLabel")}
          placeholder={t("pillbox.addMedicine.reasonPlaceholder")}
          value={reasonForTaking}
          onChange={(e) =>
            setReasonForTaking(
              category === "SOS" ? e.target.value.slice(0, 20) : e.target.value
            )
          }
          maxLength={category === "SOS" ? 20 : undefined}
          size="md"
          variant="Underlined"
        />

        <MDateInputField
          label={t("pillbox.addMedicine.startDate")}
          value={startDate}
          onDateChange={setStartDate}
          datePickerTitle={t("pillbox.addMedicine.startDate")}
          maxDate={maxAllowedDate}
          size="md"
          variant="Underlined"
        />

        <button
          type="button"
          className={styles.moreDetailsCard}
          onClick={() => setShowMoreDetails((v) => !v)}
        >
          <span className={styles.moreDetailsIcon}>
            <IconNotebook size={24} stroke={1.5} />
          </span>
          <span className={styles.moreDetailsText}>
            <span className={styles.moreDetailsTitle}>
              {t("pillbox.addMedicine.moreDetails")}
            </span>
            <span className={styles.moreDetailsSubtitle}>
              {t("pillbox.addMedicine.moreDetailsHint")}
            </span>
          </span>
          <IconChevronDown
            size={16}
            className={`${styles.moreDetailsChevron} ${
              showMoreDetails ? styles.chevronOpen : ""
            }`}
          />
        </button>

        {showMoreDetails && (
          <>
            <MDateInputField
              label={t("pillbox.addMedicine.stopDate")}
              value={endDate}
              onDateChange={setEndDate}
              datePickerTitle={t("pillbox.addMedicine.stopDate")}
              minDate={startDate}
              maxDate={maxAllowedDate}
              size="md"
              variant="Underlined"
            />
            <MInputField
              label={t("pillbox.addMedicine.doctorName")}
              placeholder={t("pillbox.addMedicine.doctorPlaceholder")}
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              size="md"
              variant="Underlined"
            />
            <MInputField
              label={t("pillbox.addMedicine.foodContext")}
              value={foodContextLabel}
              onClick={() => setFoodContextDrawerOpen(true)}
              readOnly
              size="md"
              variant="Underlined"
              icon={<IconChevronDown size={16} />}
              showIcon
              onIconClick={() => setFoodContextDrawerOpen(true)}
            />
            <MTextArea
              label={t("pillbox.addMedicine.instructions")}
              placeholder={t("pillbox.addMedicine.instructionsPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 50))}
              maxLength={50}
            />
          </>
        )}

        <MButton
          variant="filled"
          size="lg"
          text={t(
            mode === "edit"
              ? "pillbox.addMedicine.confirmButton"
              : mode === "restore"
              ? "pillbox.addMedicine.restoreButton"
              : "pillbox.addMedicine.submit"
          )}
          onClick={handleSubmit}
          disabled={!isValid}
          loading={submitting}
          className={styles.submitButton}
        />
      </div>

      <MDropDownDrawer
        open={repeatsDrawerOpen}
        onClose={() => setRepeatsDrawerOpen(false)}
        title={t("pillbox.addMedicine.repeats")}
        items={repeatsOptions}
        initialValue={repeatsOption}
        onSelect={(v) => setRepeatsOption(v as RepeatsOption)}
      />
      <MDropDownDrawer
        open={foodContextDrawerOpen}
        onClose={() => setFoodContextDrawerOpen(false)}
        title={t("pillbox.addMedicine.foodContext")}
        items={foodContextOptions}
        initialValue={foodContext}
        onSelect={(v) => setFoodContext(v as FoodContext)}
      />

      {duplicateModal && (
        <DuplicateMedicineModal
          open={!!duplicateModal}
          item={duplicateModal.item}
          variant={duplicateModal.variant}
          memberDisplayName={memberDisplayName}
          onClose={() => setDuplicateModal(null)}
          onConfirm={handleDuplicateConfirm}
        />
      )}

      <MDialog
        isOpen={showMoveToScheduledConfirm}
        onClose={() => setShowMoveToScheduledConfirm(false)}
        title={t("pillbox.moveToScheduledModal.title")}
        description={t("pillbox.moveToScheduledModal.description")}
        secondaryCtaText={t("common.cancel")}
        onSecondaryCta={() => setShowMoveToScheduledConfirm(false)}
        primaryCtaText={t("common.confirm")}
        onPrimaryCta={handleMoveToScheduledConfirm}
        isPrimaryCtaLoading={submitting}
      />
    </div>
  );
};

export default AddMedicine;
